import { CSG, CSG2, Mesh, MeshBuilder, PhysicsShapeType, Quaternion, Vector3, VertexBuffer, VertexData } from '@babylonjs/core';
import { Level, WayMesh } from './Level';
import { WayGenerationConfig } from './LevelConfig';
import { ExistingMeshConfig, MeshFactory } from './MeshFactory';

export class MeshCutter {
  /**
   * Cut holes into way meshes by directly removing triangles.
   * For each hole, an oriented bounding box (OBB) cutter is defined.
   * Any triangle whose centroid falls inside a cutter OBB is removed.
   * This avoids CSG boolean operations which require manifold/watertight meshes.
   *
   * @deprecated use CSG methods instead
   */
  static async cutHolesInWayRemovingTriangles(level: Level, config: WayGenerationConfig, wayMeshes: WayMesh[]): Promise<void> {
    console.log('[HoleCut] Starting hole cutting, wayMeshes:', wayMeshes.length);

    const angleMin = config.holeAngle?.min ?? 0;
    const angleMax = config.holeAngle?.max ?? Math.PI * 2;

    // Determine the cross-section radius from the shape or width config
    let crossSectionRadius = (config.width || 5) / 2;
    if (config.shape && config.shape.length > 0) {
      crossSectionRadius = 0;
      for (const p of config.shape) {
        const dist = Math.sqrt(p.x * p.x + p.y * p.y);
        if (dist > crossSectionRadius) crossSectionRadius = dist;
      }
    }

    for (let wi = 0; wi < wayMeshes.length; wi++) {
      const wayMesh = wayMeshes[wi];
      const pathIndexes = wayMesh.pathIndexes;
      if (pathIndexes.length < 2) continue;

      const currentMesh = wayMesh.mesh;
      let lastHolePos: Vector3 | null = null;

      // Collect all cutter OBBs for this way segment
      const cutters: { position: Vector3; halfExtents: Vector3; axisX: Vector3; axisY: Vector3; axisZ: Vector3 }[] = [];
      const holePathOffset = config.holePathOffset || 3;

      for (let si = holePathOffset; si < pathIndexes.length - holePathOffset; si++) {
        const pathIndex = pathIndexes[si];
        if (!level.pathPoints[pathIndex]) continue;

        const pathPoint = level.pathPoints[pathIndex];
        const minSpacing = (config.holeSize.max || 2) * 1.2;
        if (lastHolePos && Vector3.Distance(pathPoint, lastHolePos) < minSpacing) {
          continue;
        }

        if (level.floatInRange(0, 1) >= (config.holeProbability || 0)) continue;

        // Random hole size
        const holeWidth = level.floatInRange(config.holeSize.min, config.holeSize.max);
        const holeDepth = level.floatInRange(config.holeSize.min, config.holeSize.max);

        // Random angle around the path tangent axis
        const angle = level.floatInRange(angleMin, angleMax);

        const yOffset = config.yOffset || 0;
        const center = pathPoint.add(Vector3.Up().scale(yOffset));

        // Tangent calculation
        const nextIdx = Math.min(pathIndex + 1, level.pathPoints.length - 1);
        const prevIdx = Math.max(pathIndex - 1, 0);
        const tangent = level.pathPoints[nextIdx].subtract(level.pathPoints[prevIdx]).normalize();

        // Build Local Frame (Frenet)
        let up = Vector3.Up();
        if (Math.abs(Vector3.Dot(tangent, up)) > 0.99) {
          up = Vector3.Right();
        }
        const right = Vector3.Cross(up, tangent).normalize();
        const localUp = Vector3.Cross(tangent, right).normalize();

        // Rotate the lateral direction by angle around tangent
        const rotationQuat = Quaternion.RotationAxis(tangent, angle);
        const radialDir = Vector3.Zero();
        localUp.rotateByQuaternionToRef(rotationQuat, radialDir);

        // Cutter centered on the path, extends through full cross-section
        const position = center;

        // OBB axes: X = lateral (rotated by angle), Y = radial (through surface), Z = tangent
        const boxZ = tangent;
        const boxY = radialDir;
        const boxX = Vector3.Cross(boxY, boxZ).normalize();

        cutters.push({
          position,
          // Width (X) and depth (Z) from holeSize, height (Y) covers full cross-section
          halfExtents: new Vector3(holeWidth / 2, crossSectionRadius * 2, holeDepth / 2),
          axisX: boxX,
          axisY: boxY,
          axisZ: boxZ,
        });

        lastHolePos = pathPoint;
      }

      if (cutters.length === 0) continue;

      // Get mesh vertex data
      const positions = currentMesh.getVerticesData(VertexBuffer.PositionKind);
      const normals = currentMesh.getVerticesData(VertexBuffer.NormalKind);
      const uvs = currentMesh.getVerticesData(VertexBuffer.UVKind);
      const indices = currentMesh.getIndices();
      if (!positions || !indices) continue;

      currentMesh.computeWorldMatrix(true);
      const worldMatrix = currentMesh.getWorldMatrix();

      // Filter triangles: remove those that overlap any cutter OBB
      // Using separating axis test: project all 3 vertices onto each OBB axis
      // and check if their projection range overlaps with the cutter extents
      const keptIndices: number[] = [];
      const v0 = Vector3.Zero();
      const v1 = Vector3.Zero();
      const v2 = Vector3.Zero();

      for (let ti = 0; ti < indices.length; ti += 3) {
        const i0 = indices[ti];
        const i1 = indices[ti + 1];
        const i2 = indices[ti + 2];

        // Get triangle vertices in world space
        v0.set(positions[i0 * 3], positions[i0 * 3 + 1], positions[i0 * 3 + 2]);
        v1.set(positions[i1 * 3], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
        v2.set(positions[i2 * 3], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);
        Vector3.TransformCoordinatesToRef(v0, worldMatrix, v0);
        Vector3.TransformCoordinatesToRef(v1, worldMatrix, v1);
        Vector3.TransformCoordinatesToRef(v2, worldMatrix, v2);

        // Test against all cutters
        let insideCutter = false;
        for (const cutter of cutters) {
          // Project each vertex into cutter's local space via dot products
          const d0x = v0.x - cutter.position.x;
          const d0y = v0.y - cutter.position.y;
          const d0z = v0.z - cutter.position.z;
          const d1x = v1.x - cutter.position.x;
          const d1y = v1.y - cutter.position.y;
          const d1z = v1.z - cutter.position.z;
          const d2x = v2.x - cutter.position.x;
          const d2y = v2.y - cutter.position.y;
          const d2z = v2.z - cutter.position.z;

          // Project onto axisX
          const pX0 = d0x * cutter.axisX.x + d0y * cutter.axisX.y + d0z * cutter.axisX.z;
          const pX1 = d1x * cutter.axisX.x + d1y * cutter.axisX.y + d1z * cutter.axisX.z;
          const pX2 = d2x * cutter.axisX.x + d2y * cutter.axisX.y + d2z * cutter.axisX.z;
          const minX = Math.min(pX0, pX1, pX2);
          const maxX = Math.max(pX0, pX1, pX2);
          if (minX > cutter.halfExtents.x || maxX < -cutter.halfExtents.x) continue;

          // Project onto axisY
          const pY0 = d0x * cutter.axisY.x + d0y * cutter.axisY.y + d0z * cutter.axisY.z;
          const pY1 = d1x * cutter.axisY.x + d1y * cutter.axisY.y + d1z * cutter.axisY.z;
          const pY2 = d2x * cutter.axisY.x + d2y * cutter.axisY.y + d2z * cutter.axisY.z;
          const minY = Math.min(pY0, pY1, pY2);
          const maxY = Math.max(pY0, pY1, pY2);
          if (minY > cutter.halfExtents.y || maxY < -cutter.halfExtents.y) continue;

          // Project onto axisZ
          const pZ0 = d0x * cutter.axisZ.x + d0y * cutter.axisZ.y + d0z * cutter.axisZ.z;
          const pZ1 = d1x * cutter.axisZ.x + d1y * cutter.axisZ.y + d1z * cutter.axisZ.z;
          const pZ2 = d2x * cutter.axisZ.x + d2y * cutter.axisZ.y + d2z * cutter.axisZ.z;
          const minZ = Math.min(pZ0, pZ1, pZ2);
          const maxZ = Math.max(pZ0, pZ1, pZ2);
          if (minZ > cutter.halfExtents.z || maxZ < -cutter.halfExtents.z) continue;

          // Overlap on all 3 axes → triangle intersects the cutter
          insideCutter = true;
          break;
        }

        if (!insideCutter) {
          keptIndices.push(i0, i1, i2);
        }
      }

      const removedTriangles = (indices.length - keptIndices.length) / 3;
      if (removedTriangles === 0) {
        console.log(`[HoleCut] Way ${wi}: NO triangles removed!`);
        continue;
      }

      console.log(`[HoleCut] Removed ${removedTriangles} triangles with ${cutters.length} cutters`);

      // Rebuild mesh with filtered indices
      const savedMaterial = currentMesh.material;
      const resultMesh = new Mesh(currentMesh.name + '_holed', level.scene);
      const vData = new VertexData();
      vData.positions = positions;
      vData.indices = keptIndices;
      if (normals) vData.normals = normals;
      if (uvs) vData.uvs = uvs;
      vData.applyToMesh(resultMesh);

      resultMesh.material = savedMaterial;
      resultMesh.checkCollisions = true;
      resultMesh.isPickable = false;
      resultMesh.metadata = currentMesh.metadata;

      // Re-configure physics
      const meshConfig = ExistingMeshConfig.builder()
        .name('way')
        .physicsShapeType(PhysicsShapeType.MESH)
        .receiveShadows(true)
        .mesh(resultMesh)
        .build();
      MeshFactory.configureMesh(resultMesh, meshConfig, level);

      currentMesh.dispose();
      wayMeshes[wi] = { mesh: resultMesh, pathIndexes: pathIndexes };
    }
  }

  /**
   * Cut holes into way meshes using Legacy CSG boolean subtraction.
   * Holes are positioned at random path points along each segment and rotated
   * around the path tangent axis by a random angle, enabling holes on all sides
   * of tunnels, halfpipes, and flat surfaces.
   */
  static async cutHolesInWayCSGLegacy(level: Level, config: WayGenerationConfig, wayMeshes: WayMesh[]): Promise<void> {
    const angleMin = config.holeAngle?.min ?? 0;
    const angleMax = config.holeAngle?.max ?? Math.PI * 2;

    // Determine the cross-section radius from the shape or width config
    // This tells us how far from the path center the mesh surface is
    let crossSectionRadius = (config.width || 5) / 2;
    if (config.shape && config.shape.length > 0) {
      // Find max distance from origin in the shape definition
      crossSectionRadius = 0;
      for (const p of config.shape) {
        const dist = Math.sqrt(p.x * p.x + p.y * p.y);
        if (dist > crossSectionRadius) crossSectionRadius = dist;
      }
    }

    for (let wi = 0; wi < wayMeshes.length; wi++) {
      const wayMesh = wayMeshes[wi];
      const pathIndexes = wayMesh.pathIndexes;
      if (pathIndexes.length < 2) continue;

      let currentMesh = wayMesh.mesh;
      let holesCreated = 0;
      let lastHolePos: Vector3 | null = null;

      // Batch CSG: Convert Mesh to CSG once
      let wayCSG = CSG.FromMesh(currentMesh);
      let modified = false;

      const holePathOffset = config.holePathOffset || 3;

      for (let si = holePathOffset; si < pathIndexes.length - holePathOffset; si++) {
        const pathIndex = pathIndexes[si];
        if (!level.pathPoints[pathIndex]) continue;

        // Skip if too close to last hole (prevents messy overlapping CSG)
        const pathPoint = level.pathPoints[pathIndex];
        // Use max hole size + margin as safety distance
        const minSpacing = (config.holeSize.max || 2) * 1.2;
        if (lastHolePos && Vector3.Distance(pathPoint, lastHolePos) < minSpacing) {
          continue;
        }

        if (level.floatInRange(0, 1) >= (config.holeProbability || 0)) continue;

        // Random hole size
        const holeWidth = level.floatInRange(config.holeSize.min, config.holeSize.max);
        const holeDepth = level.floatInRange(config.holeSize.min, config.holeSize.max);
        // Height must be large enough to fully penetrate through the mesh wall
        const holeHeight = crossSectionRadius * 3; // Large enough to penetrate wall

        // Random angle around the path tangent axis
        const angle = level.floatInRange(angleMin, angleMax);

        // Position the cutter at the mesh surface:
        // center it at the path point, offset radially by the cross-section radius
        const yOffset = config.yOffset || 0;
        const center = pathPoint.add(Vector3.Up().scale(yOffset));

        // Tangent calculation
        const nextIdx = Math.min(pathIndex + 1, level.pathPoints.length - 1);
        const prevIdx = Math.max(pathIndex - 1, 0);
        const tangent = level.pathPoints[nextIdx].subtract(level.pathPoints[prevIdx]).normalize(); // Z axis (local)

        // Build Local Frame (Frenet)
        let up = Vector3.Up();
        if (Math.abs(Vector3.Dot(tangent, up)) > 0.99) {
          up = Vector3.Right();
        }
        const right = Vector3.Cross(up, tangent).normalize(); // X axis (local)
        const localUp = Vector3.Cross(tangent, right).normalize(); // Y axis (local)

        // Calculate Radial Direction based on Angle
        // Rotate "localUp" around "tangent" by "angle"
        // 0 angle = localUp (Top)
        const rotationQuat = Quaternion.RotationAxis(tangent, angle);
        const radialDir = Vector3.Zero();
        localUp.rotateByQuaternionToRef(rotationQuat, radialDir);

        // Cutter Position
        // Offset from center by CrossSectionRadius in Radial Direction
        // The cutter box is centered at its pivot. It needs to penetrate the wall.
        const position = center.add(radialDir.scale(crossSectionRadius));

        // Create Box Cutter (Legacy CSG handles MeshBuilder.CreateBox fine)
        const cutter = MeshBuilder.CreateBox(
          'hole_cutter',
          {
            width: holeWidth,
            height: holeHeight,
            depth: holeDepth,
          },
          level.scene
        );

        // Align Cutter Rotation
        // Box default: X=Width, Y=Height, Z=Depth
        const boxZ = tangent;
        const boxY = radialDir;
        const boxX = Vector3.Cross(boxY, boxZ).normalize();

        const orientation = Quaternion.RotationQuaternionFromAxis(boxX, boxY, boxZ);
        cutter.rotationQuaternion = orientation;
        cutter.position = position;

        // Force compute world matrix before CSG
        cutter.computeWorldMatrix(true);
        currentMesh.computeWorldMatrix(true);

        // Add to batch
        const cutterCSG = CSG.FromMesh(cutter);
        const isSolidShape = config.shape && (config.shape as any).solidHalfCount > 0;
        wayCSG = isSolidShape ? wayCSG.union(cutterCSG) : wayCSG.subtract(cutterCSG);

        modified = true;

        holesCreated++;
        lastHolePos = pathPoint;

        cutter.dispose();
      }

      if (modified) {
        const savedMaterial = currentMesh.material;
        // Reconstruct final mesh
        const resultMesh = wayCSG.toMesh(currentMesh.name + '_holed', savedMaterial, level.scene);
        resultMesh.checkCollisions = true;
        resultMesh.isPickable = false;
        resultMesh.metadata = currentMesh.metadata;

        // Re-configure physics
        const meshConfig = ExistingMeshConfig.builder()
          .name('way')
          .physicsShapeType(PhysicsShapeType.MESH)
          .receiveShadows(true)
          .mesh(resultMesh)
          .build();
        MeshFactory.configureMesh(resultMesh, meshConfig, level);

        currentMesh.dispose();
        wayMeshes[wi] = { mesh: resultMesh, pathIndexes: pathIndexes };
      }
    }
  }

  static async cutHolesInWayCSG(level: Level, config: WayGenerationConfig, wayMeshes: WayMesh[]): Promise<void> {
    const angleMin = config.holeAngle?.min ?? 0;
    const angleMax = config.holeAngle?.max ?? Math.PI * 2;

    // Determine the cross-section radius from the shape or width config
    // This tells us how far from the path center the mesh surface is
    let crossSectionRadius = (config.width || 5) / 2;
    if (config.shape && config.shape.length > 0) {
      // Find max distance from origin in the shape definition
      crossSectionRadius = 0;
      for (const p of config.shape) {
        const dist = Math.sqrt(p.x * p.x + p.y * p.y);
        if (dist > crossSectionRadius) crossSectionRadius = dist;
      }
    }

    for (let wi = 0; wi < wayMeshes.length; wi++) {
      const wayMesh = wayMeshes[wi];
      const pathIndexes = wayMesh.pathIndexes;
      if (pathIndexes.length < 2) continue;

      let currentMesh = wayMesh.mesh;
      let holesCreated = 0;
      let lastHolePos: Vector3 | null = null;

      // Batch CSG: Convert Mesh to CSG once
      let wayCSG = CSG2.FromMesh(currentMesh);
      let modified = false;

      const holePathOffset = config.holePathOffset || 3;

      for (let si = holePathOffset; si < pathIndexes.length - holePathOffset; si++) {
        const pathIndex = pathIndexes[si];
        if (!level.pathPoints[pathIndex]) continue;

        // Skip if too close to last hole (prevents messy overlapping CSG)
        const pathPoint = level.pathPoints[pathIndex];
        // Use max hole size + margin as safety distance
        const minSpacing = (config.holeSize.max || 2) * 1.2;
        if (lastHolePos && Vector3.Distance(pathPoint, lastHolePos) < minSpacing) {
          continue;
        }

        if (level.floatInRange(0, 1) >= (config.holeProbability || 0)) continue;

        // Random hole size
        const holeWidth = level.floatInRange(config.holeSize.min, config.holeSize.max);
        const holeDepth = level.floatInRange(config.holeSize.min, config.holeSize.max);
        // Height must be large enough to fully penetrate through the mesh wall
        const holeHeight = crossSectionRadius * 3; // Large enough to penetrate wall

        // Random angle around the path tangent axis
        const angle = level.floatInRange(angleMin, angleMax);

        // Position the cutter at the mesh surface:
        // center it at the path point, offset radially by the cross-section radius
        const yOffset = config.yOffset || 0;
        const center = pathPoint.add(Vector3.Up().scale(yOffset));

        // Tangent calculation
        const nextIdx = Math.min(pathIndex + 1, level.pathPoints.length - 1);
        const prevIdx = Math.max(pathIndex - 1, 0);
        const tangent = level.pathPoints[nextIdx].subtract(level.pathPoints[prevIdx]).normalize(); // Z axis (local)

        // Build Local Frame (Frenet)
        let up = Vector3.Up();
        if (Math.abs(Vector3.Dot(tangent, up)) > 0.99) {
          up = Vector3.Right();
        }
        const right = Vector3.Cross(up, tangent).normalize(); // X axis (local)
        const localUp = Vector3.Cross(tangent, right).normalize(); // Y axis (local)

        // Calculate Radial Direction based on Angle
        // Rotate "localUp" around "tangent" by "angle"
        // 0 angle = localUp (Top)
        const rotationQuat = Quaternion.RotationAxis(tangent, angle);
        const radialDir = Vector3.Zero();
        localUp.rotateByQuaternionToRef(rotationQuat, radialDir);

        // Cutter Position
        // Offset from center by CrossSectionRadius in Radial Direction
        // The cutter box is centered at its pivot. It needs to penetrate the wall.
        const position = center.add(radialDir.scale(crossSectionRadius));

        // Create Box Cutter (Legacy CSG handles MeshBuilder.CreateBox fine)
        const cutter = MeshBuilder.CreateBox(
          'hole_cutter',
          {
            width: holeWidth,
            height: holeHeight,
            depth: holeDepth,
          },
          level.scene
        );

        // Align Cutter Rotation
        // Box default: X=Width, Y=Height, Z=Depth
        const boxZ = tangent;
        const boxY = radialDir;
        const boxX = Vector3.Cross(boxY, boxZ).normalize();

        const orientation = Quaternion.RotationQuaternionFromAxis(boxX, boxY, boxZ);
        cutter.rotationQuaternion = orientation;
        cutter.position = position;

        // Force compute world matrix before CSG
        cutter.computeWorldMatrix(true);
        currentMesh.computeWorldMatrix(true);

        // Add to batch
        const cutterCSG = CSG2.FromMesh(cutter);
        wayCSG = wayCSG.subtract(cutterCSG);

        modified = true;

        holesCreated++;
        lastHolePos = pathPoint;

        cutter.dispose();
      }

      if (modified) {
        const savedMaterial = currentMesh.material;
        // Reconstruct final mesh
        const resultMesh = wayCSG.toMesh(currentMesh.name + '_holed', level.scene);
        resultMesh.material = savedMaterial;
        resultMesh.checkCollisions = true;
        resultMesh.isPickable = false;
        resultMesh.metadata = currentMesh.metadata;

        // Re-configure physics
        const meshConfig = ExistingMeshConfig.builder()
          .name('way')
          .physicsShapeType(PhysicsShapeType.MESH)
          .receiveShadows(true)
          .mesh(resultMesh)
          .build();
        MeshFactory.configureMesh(resultMesh, meshConfig, level);

        currentMesh.dispose();
        wayMeshes[wi] = { mesh: resultMesh, pathIndexes: pathIndexes };
      }
    }
  }
}
